import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useVocabulary } from './contexts/VocabularyContext';
import QuizTitleEditor from './components/QuizTitleEditor';
import './css/Vocabulary.css';
import './css/VocabularyQuiz.css';

const VocabularyQuizPlay = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quizId } = useParams();
  const { quizApi } = useVocabulary();

  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  // 서버에 저장된 답 (question_id → 값). 정오는 담기지 않는다
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const questionStart = useRef(Date.now());

  // 새로고침·뒤로가기로 들어와도 서버 상태에서 그대로 복구된다
  useEffect(() => {
    const load = async () => {
      const data = await quizApi.getQuiz(quizId);
      if (!data.success) {
        toast.error(t('vocabulary.quiz.notFound'));
        navigate('/vocabulary/quiz');
        return;
      }
      if (data.quiz.status === 'completed') {
        navigate(`/vocabulary/quiz/${quizId}/result`, { replace: true });
        return;
      }

      setQuiz(data.quiz);
      setQuestions(data.questions);
      const restored = {};
      data.questions.forEach(q => {
        if (q.answered) restored[q.question_id] = q.user_answer;
      });
      setAnswers(restored);

      // 아직 답하지 않은 첫 문항부터 이어서 푼다
      const next = data.questions.findIndex(q => !q.answered);
      setIndex(next === -1 ? 0 : next);
      setLoading(false);
      await quizApi.startQuiz(quizId);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  useEffect(() => {
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const current = questions[index];

  // 문항이 바뀌면 입력창을 그 문항의 저장된 답으로 되돌린다
  useEffect(() => {
    if (!current) return;
    const saved = answers[current.question_id];
    setInput(saved === undefined || saved === null ? '' : String(saved));
    questionStart.current = Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, current?.question_id]);

  const save = useCallback(async (value) => {
    if (!current) return;
    const spent = Math.max(1, Math.round((Date.now() - questionStart.current) / 1000));
    const data = await quizApi.submitAnswer(quizId, {
      question_id: current.question_id,
      user_answer: value,
      time_spent_seconds: spent,
    });
    if (data.success) {
      setAnswers(prev => ({ ...prev, [current.question_id]: String(value) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, quizId]);

  // 주관식은 입력 중이던 값을 화면을 벗어나기 전에 저장한다
  const flushInput = useCallback(async () => {
    if (current && current.format === 'short_answer' && input.trim() !== '') {
      await save(input.trim());
    }
  }, [current, input, save]);

  const goTo = async (nextIndex) => {
    await flushInput();
    setIndex(Math.max(0, Math.min(questions.length - 1, nextIndex)));
  };

  /**
   * 답은 문항 단위로 서버에 저장되므로 중간에 나가도 잃지 않는다.
   * 목록에서 '이어서 풀기'로 그대로 돌아온다.
   */
  const leaveTo = async (path) => {
    await flushInput();
    navigate(path);
  };

  const handleRename = async (nextTitle) => {
    const data = await quizApi.updateQuiz(quizId, { title: nextTitle });
    if (!data.success) {
      toast.error(t('error.save'));
      return false;
    }
    setQuiz(prev => ({ ...prev, title: data.title }));
    return true;
  };

  const pickChoice = async (choiceIndex) => {
    setInput(String(choiceIndex));
    await save(choiceIndex);
  };

  const answeredCount = Object.keys(answers).length;

  const handleFinish = async () => {
    if (current && current.format === 'short_answer' && input.trim() !== '') {
      await save(input.trim());
    }
    const unanswered = questions.length - Object.keys(answers).length;
    const message = unanswered > 0
      ? t('vocabulary.quiz.finishWithUnanswered', { count: unanswered })
      : t('vocabulary.quiz.finishConfirm');
    if (!window.confirm(message)) return;

    setSubmitting(true);
    const data = await quizApi.finishQuiz(quizId);
    setSubmitting(false);
    if (data.success) {
      navigate(`/vocabulary/quiz/${quizId}/result`, { replace: true });
    } else {
      toast.error(t('error.save'));
    }
  };

  const formatTime = (sec) =>
    `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

  if (loading) {
    return <div className="vocab-container"><p className="vocab-empty">{t('vocabulary.loading')}</p></div>;
  }

  return (
    <div className="vocab-container quiz-play">
      {/* 나가기 · 제목 · 문제 전체 보기 */}
      <div className="quiz-play-top">
        <button className="vocab-btn" onClick={() => leaveTo('/vocabulary/quiz')}>
          &#8592; {t('vocabulary.quiz.backToList')}
        </button>
        <QuizTitleEditor
          title={quiz?.title}
          onSave={handleRename}
          className="quiz-play-title"
        />
        <button
          className="vocab-btn"
          onClick={() => leaveTo(`/vocabulary/quiz/${quizId}/paper`)}
          title={t('vocabulary.quiz.viewAllHint')}
        >
          {t('vocabulary.quiz.viewAllQuestions')}
        </button>
      </div>

      <div className="quiz-play-header">
        <div className="quiz-progress-info">
          <strong>{index + 1} / {questions.length}</strong>
          <span className="quiz-answered">
            {t('vocabulary.quiz.answeredCount', { count: answeredCount })}
          </span>
        </div>
        <span className="quiz-timer">{formatTime(elapsed)}</span>
      </div>

      <div className="quiz-progress-bar">
        <div className="quiz-progress-fill" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
      </div>

      {/* 문항 번호 네비게이션 — 답한 문항은 채워진 상태로만 표시하고 정오는 드러내지 않는다 */}
      <div className="quiz-nav-dots">
        {questions.map((q, i) => (
          <button
            key={q.question_id}
            className={`quiz-dot${i === index ? ' current' : ''}${answers[q.question_id] !== undefined ? ' answered' : ''}`}
            onClick={() => goTo(i)}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {current && (
        <div className="quiz-card">
          <div className="quiz-card-meta">
            <span className="quiz-badge">
              {current.direction === 'word_to_meaning'
                ? t('vocabulary.quiz.wordToMeaning')
                : t('vocabulary.quiz.meaningToWord')}
            </span>
            {current.study_date && <span className="quiz-date-badge">{current.study_date}</span>}
          </div>

          <p className="quiz-prompt">{current.prompt}</p>

          {current.format === 'multiple_choice' ? (
            <ul className="quiz-choices">
              {current.choices.map((choice, i) => (
                <li key={i}>
                  <button
                    className={`quiz-choice${String(input) === String(i) ? ' picked' : ''}`}
                    onClick={() => pickChoice(i)}
                  >
                    <span className="quiz-choice-num">{i + 1}</span>
                    {choice}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="quiz-short-answer">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') goTo(index + 1); }}
                placeholder={current.direction === 'meaning_to_word'
                  ? t('vocabulary.quiz.enterWord')
                  : t('vocabulary.quiz.enterMeaning')}
                autoFocus
              />
              <button className="vocab-btn" onClick={() => save(input.trim())} disabled={input.trim() === ''}>
                {t('save')}
              </button>
            </div>
          )}

          {answers[current.question_id] !== undefined && (
            <p className="quiz-saved-note">{t('vocabulary.quiz.saved')}</p>
          )}
        </div>
      )}

      <div className="quiz-play-actions">
        <button className="vocab-btn" onClick={() => goTo(index - 1)} disabled={index === 0}>
          &#8592; {t('vocabulary.quiz.prev')}
        </button>
        {index < questions.length - 1 ? (
          <button className="vocab-btn vocab-btn-primary" onClick={() => goTo(index + 1)}>
            {t('vocabulary.quiz.next')} &#8594;
          </button>
        ) : (
          <button className="vocab-btn vocab-btn-primary" onClick={handleFinish} disabled={submitting}>
            {submitting ? t('vocabulary.loading') : t('vocabulary.quiz.finish')}
          </button>
        )}
      </div>

      <button className="quiz-finish-link" onClick={handleFinish} disabled={submitting}>
        {t('vocabulary.quiz.finishNow')}
      </button>
    </div>
  );
};

export default VocabularyQuizPlay;
